import * as React from 'react';
import { isMobile } from 'react-device-detect';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Swiper } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

const OuterWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space.s};
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  max-width: 100%;
  width: 100%;

  .swiper-pagination-bullets {
    bottom: 0;
  }

  .swiper-pagination-bullet {
    pointer-events: none;
    width: 6px;
    height: 6px;
    border-radius: 6px;
    background-color: ${({ theme }) => theme.colors.elvitaMidGray};
    opacity: 1;
    margin: 0 1.5px !important;
  }

  .swiper-pagination-bullet-active {
    background-color: ${({ theme }) => theme.colors.elvitaWebPurple};
  }
`;

const StyledSwiper = styled(Swiper)<{ $isStartPage?: boolean }>`
  width: 100%;
  min-width: ${({ $isStartPage }) => $isStartPage && '400px'};
  margin-top: ${({ $isStartPage }) => $isStartPage && '20px'};
  height: ${({ $isStartPage }) => ($isStartPage ? '330px' : '260px')};
`;

const NavigationItem = styled.div<{ $hide?: boolean; $showCaret?: boolean }>`
  display: flex;
  height: 100%;
  align-items: center;
  position: absolute;
  z-index: 8;
  cursor: pointer;
  left: 0;
  margin-left: 2%;
  opacity: ${({ $showCaret }) => ($showCaret ? '1' : '0')};
  visibility: ${({ $hide }) => ($hide ? 'hidden' : 'visible')};

  /* Show when any child has focus */
  &:focus-within {
    opacity: 1;
    transition: opacity 0s; /* Instant appearance on focus */
  }

  &:nth-child(2) {
    transform: rotate(180deg);
    left: unset;
    right: 2%;
  }

  @media (min-width: ${({ theme }) => theme.screens.sm}) {
    margin-left: 16%;

    &:nth-child(2) {
      right: 16%;
    }
  }
`;

const Circle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding-right: 3px;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.elvitaWebPurple};
    outline-offset: 2px;
  }

  &:hover {
    opacity: 0.7;
  }
  border-radius: 35px;
  background-color: rgba(0, 0, 0, 0.1);
`;

type CarouselProps = {
  onIndexChange?(index: number): void;
  onSwiperInit?(swiper: SwiperType): void;
  itemsLength: number;
  className?: string;
  isStartPage?: boolean;
  children: React.ReactNode;
};

const Carousel: React.FC<CarouselProps> = ({
  children,
  onIndexChange,
  onSwiperInit,
  itemsLength,
  className,
  isStartPage,
}) => {
  const [index, setIndex] = React.useState(0);
  const [showCaret, setShowCaret] = React.useState(false);
  const swiperRef = React.useRef<SwiperType | null>(null);
  const location = useLocation();

  const handleIndexChange = (index: number) => {
    setIndex(index);
    if (onIndexChange) onIndexChange(index);
  };

  const handleFocusIn = () => {
    setShowCaret(true);
  };

  const handleFocusOut = () => {
    setShowCaret(false);
  };

  React.useEffect(() => {
    setIndex(0);
    swiperRef.current?.slideTo(0);
  }, [location]);

  return (
    <OuterWrapper>
      <Wrapper
        onMouseEnter={() => setShowCaret(true)}
        onMouseLeave={() => setShowCaret(false)}
      >
        <NavigationItem
          className={`prev-${className}`}
          $hide={!index || isMobile}
          $showCaret={showCaret}
          onMouseEnter={() => setShowCaret(true)}
        >
          <Circle
            aria-label="Previous slide"
            onFocus={handleFocusIn}
            onBlur={handleFocusOut}
          >
            <img src="/images/caret.svg" alt="" />
          </Circle>
        </NavigationItem>
        <NavigationItem
          className={`next-${className}`}
          $hide={index === itemsLength - 1 || isMobile}
          $showCaret={showCaret}
          onMouseEnter={() => setShowCaret(true)}
        >
          <Circle
            aria-label="Next slide"
            onFocus={handleFocusIn}
            onBlur={handleFocusOut}
          >
            <img src="/images/caret.svg" alt="" />
          </Circle>
        </NavigationItem>
        <StyledSwiper
          $isStartPage={isStartPage}
          modules={[Navigation, Pagination]}
          slidesPerView={1.2}
          centeredSlides={true}
          loop={false}
          speed={300}
          pagination={{
            clickable: false,
            dynamicBullets: false,
          }}
          spaceBetween={15}
          onSlideChange={({ realIndex }) => handleIndexChange(realIndex)}
          navigation={{
            prevEl: `.prev-${className}`,
            nextEl: `.next-${className}`,
          }}
          className={className}
          onSwiper={swiper => {
            swiperRef.current = swiper;
            if (onSwiperInit) onSwiperInit(swiper);
          }}
        >
          {children}
        </StyledSwiper>
      </Wrapper>
    </OuterWrapper>
  );
};

export default Carousel;
